import { useAuth0 } from "@auth0/auth0-react";
import React, { FC, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import Button from "@laborhack/custom-button";
import {
  Link,
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import { Divider } from "_components/Divider";
import { useApplicationConfig } from "../../../context/ApplicationConfig";
import { logEvent } from "../../../helpers/analytics";
import { useQuery } from "../../../hooks";
import EmailLinkSignInForm from "../components/EmailLinkSignIn";
import FederatedIdentitySignInForm from "../components/FederatedIDPSignIn";

import businessCardImage from "assets/business-user-card.svg";
import artisanCardImage from "assets/artisan-user-card.svg";
import homeOwnerCardImage from "assets/homeowner-user-card.svg";

import styles from "./styles.module.scss";
import UserTypeCard from "../components/UserTypeCard";
import clsx from "clsx";
import ArtisanSignIn from "../components/ArtisanSignIn";
import { Icon } from "semantic-ui-react";

export interface LoginProps {}

export const Login: FC<LoginProps> = ({}) => {
  const { loginWithRedirect, isLoading } = useAuth0();

  const { toggles } = useApplicationConfig();

  const { pathname } = useLocation();

  const { path } = useRouteMatch();

  /**
   * Get the current user type route from url
   * /auth/register/<THIS_ITEM>
   */
  const selectedUserType = pathname.split("/")[3];

  const history = useHistory();
  const query = useQuery();
  const hideSelection = Boolean(query.get("hide_selection"));

  const continueUrl = query.get("continue");

  continueUrl && localStorage.setItem("continue_url_after_signin", continueUrl);

  const [userType, setUserType] = useState<string>(selectedUserType);

  const userTypeCardProps = [
    {
      text: "Company",
      image: businessCardImage,
      className: "bg-[#2978B1]",
      inactiveBgColor: "bg-[#D2E8E9]",
      value: "business",
    },
    {
      text: "Artisan",
      image: artisanCardImage,
      className: "bg-[#29B19A]",
      inactiveBgColor: "bg-[#D8EBDF]",
      value: "pro",
    },
    {
      text: "Homeowner",
      image: homeOwnerCardImage,
      className: "bg-accent-500",
      inactiveBgColor: "bg-[#F5E0D5]",
      value: "homeowner",
    },
  ];

  const handleUserTypeChange = (value: string) => {
    setUserType(value);
    history.push(`${path.replace("/:id", "")}/${value}`);
  };

  continueUrl && localStorage.setItem("continue_url_after_signin", continueUrl);

  useEffect(() => {
    logEvent("page_view");
  }, []);

  const handleBusinessLogin = () => {
    loginWithRedirect({
      redirectUri: process.env.REACT_APP_MARKETPLACE_URL,
    });
  };

  const handleHomeownerLogin = () => {
    loginWithRedirect({
      redirectUri: window.location.origin + "/auth/finishSignIn",
    });
  };

  return (
    <>
      <Helmet>
        <title>Login | LaborHack</title>
      </Helmet>
      <div className={styles.login}>
        <h1 className="text-3xl mb-4">Welcome Back</h1>
        <p>
          Don&apos;t have an account <Link to="/auth/register">Sign Up</Link>
        </p>

        <div className="my-8">
          {!hideSelection && (
            <>
              <h2 className="text-xl font-semibold text-primary-500 mb-4">
                Sign in as:
              </h2>
              <div className="flex flex-col md:flex-row">
                {userTypeCardProps.map((props) => {
                  const isActive = userType === props.value;

                  return (
                    <UserTypeCard
                      key={props.text}
                      {...props}
                      isActive={isActive}
                      className={clsx(
                        props.className,
                        !isActive && `${props.inactiveBgColor}`
                      )}
                      onClick={() => handleUserTypeChange(props.value)}
                    />
                  );
                })}
              </div>
            </>
          )}
        </div>

        <Switch>
          <Route path={`${path}/homeowner`}>
            {toggles?.use_auth0_explicitly ? (
              <div>
                <h3 className="text-primary-500 font-bold mb-2">
                  Homeowner Sign In
                </h3>
                <Button loading={isLoading} onClick={handleHomeownerLogin}>
                  Sign in to LaborHack
                  <Icon size="large" name="long arrow alternate right" />
                </Button>
              </div>
            ) : (
              <>
                <FederatedIdentitySignInForm />
                <Divider className={styles.divider} text="or" />
                <EmailLinkSignInForm />{" "}
              </>
            )}
          </Route>
          <Route path={`${path}/pro`}>
            <ArtisanSignIn />
          </Route>
          <Route path={`${path}/business`}>
            <div>
              <h3 className="text-primary-500 font-bold mb-2">
                Company Sign In
              </h3>
              <Button loading={isLoading} onClick={handleBusinessLogin}>
                Sign in to LaborHack Marketplace
                <Icon size="large" name="long arrow alternate right" />
              </Button>
            </div>
          </Route>
        </Switch>
      </div>
    </>
  );
};
